import { Avatar as A } from 'radix-ui';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';

// A3 palette: blue, saffron, sea-teal, slate. No red (it reads as an error next to a name).
const tints = [
  'bg-primary-soft text-primary-ink',
  'bg-accent-soft text-accent-ink',
  'bg-area-messages-soft text-area-messages-ink',
  'bg-info-soft text-info-ink',
  'bg-sunken text-ink-soft',
];

function hash(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const sizes = {
  xs: 'size-6 text-[0.625rem]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-lg',
};

/** Initials avatar with a deterministic tint (same person, same colour). */
export function Avatar({
  name,
  src,
  size = 'sm',
  className,
}: {
  name?: string | null;
  src?: string | null;
  size?: keyof typeof sizes;
  className?: string;
}) {
  const tint = tints[hash(name ?? '?') % tints.length];
  return (
    <A.Root
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-bold',
        sizes[size],
        tint,
        className,
      )}
    >
      {src && <A.Image src={src} alt="" className="size-full object-cover" />}
      <A.Fallback delayMs={src ? 300 : 0} aria-label={name ?? undefined}>
        {initials(name)}
      </A.Fallback>
    </A.Root>
  );
}
